// src/components/TypingKeyboard.jsx
import React from 'react';
import { theme } from '../utils/theme';

const C = theme.colors;

const FINGERS = {
  LP: { name: 'Left Pinky', color: '#FF6B6B' },
  LR: { name: 'Left Ring', color: '#FFD93D' },
  LM: { name: 'Left Middle', color: '#6BCB77' },
  LI: { name: 'Left Index', color: '#4D96FF' },
  RI: { name: 'Right Index', color: '#4D96FF' },
  RM: { name: 'Right Middle', color: '#6BCB77' },
  RR: { name: 'Right Ring', color: '#FFD93D' },
  RP: { name: 'Right Pinky', color: '#FF6B6B' },
  Thumb: { name: 'Thumb', color: '#CED4DA' }
};

const KEYBOARD_LAYOUT = [
  // Row 1
  [
    { code: 'Backquote', eng: '`', engShift: '~', hindi: ']', hindiShift: 'ज्ञ', finger: 'LP' },
    { code: 'Digit1', eng: '1', engShift: '!', hindi: '1', hindiShift: '!', finger: 'LP' },
    { code: 'Digit2', eng: '2', engShift: '@', hindi: '2', hindiShift: '@', finger: 'LR' },
    { code: 'Digit3', eng: '3', engShift: '#', hindi: '3', hindiShift: '#', finger: 'LM' },
    { code: 'Digit4', eng: '4', engShift: '$', hindi: '4', hindiShift: '$', finger: 'LI' },
    { code: 'Digit5', eng: '5', engShift: '%', hindi: '5', hindiShift: '%', finger: 'LI' },
    { code: 'Digit6', eng: '6', engShift: '^', hindi: '6', hindiShift: '^', finger: 'RI' },
    { code: 'Digit7', eng: '7', engShift: '&', hindi: '7', hindiShift: '&', finger: 'RI' },
    { code: 'Digit8', eng: '8', engShift: '*', hindi: '8', hindiShift: '*', finger: 'RM' },
    { code: 'Digit9', eng: '9', engShift: '(', hindi: '9', hindiShift: '(', finger: 'RR' },
    { code: 'Digit0', eng: '0', engShift: ')', hindi: '0', hindiShift: ')', finger: 'RP' },
    { code: 'Minus', eng: '-', engShift: '_', hindi: 'ड', hindiShift: 'ढ', finger: 'RP' },
    { code: 'Equal', eng: '=', engShift: '+', hindi: 'ृ', hindiShift: 'ऋ', finger: 'RP' },
    { code: 'Backspace', eng: 'Delete', width: '80px', finger: 'RP' }
  ],
  // Row 2
  [
    { code: 'Tab', eng: 'Tab', width: '60px', finger: 'LP' },
    { code: 'KeyQ', eng: 'q', engShift: 'Q', hindi: 'ु', hindiShift: 'फ', finger: 'LP' },
    { code: 'KeyW', eng: 'w', engShift: 'W', hindi: 'ू', hindiShift: 'ॅ', finger: 'LR' },
    { code: 'KeyE', eng: 'e', engShift: 'E', hindi: 'म', hindiShift: 'म्', finger: 'LM' },
    { code: 'KeyR', eng: 'r', engShift: 'R', hindi: 'त', hindiShift: 'त्', finger: 'LI' },
    { code: 'KeyT', eng: 't', engShift: 'T', hindi: 'ज', hindiShift: 'ज्', finger: 'LI' },
    { code: 'KeyY', eng: 'y', engShift: 'Y', hindi: 'ल', hindiShift: 'ल्', finger: 'RI' },
    { code: 'KeyU', eng: 'u', engShift: 'U', hindi: 'न', hindiShift: 'न्', finger: 'RI' },
    { code: 'KeyI', eng: 'i', engShift: 'I', hindi: 'प', hindiShift: 'प्', finger: 'RM' },
    { code: 'KeyO', eng: 'o', engShift: 'O', hindi: 'य', hindiShift: 'य्', finger: 'RR' },
    { code: 'KeyP', eng: 'p', engShift: 'P', hindi: 'च', hindiShift: 'च्', finger: 'RP' },
    { code: 'BracketLeft', eng: '[', engShift: '{', hindi: 'ख्', hindiShift: 'क्ष', finger: 'RP' },
    { code: 'BracketRight', eng: ']', engShift: '}', hindi: 'comma', hindiShift: 'द्व', finger: 'RP' },
    { code: 'Backslash', eng: '\\', engShift: '|', hindi: 'ा', hindiShift: 'ा', finger: 'RP' }
  ],
  // Row 3
  [
    { code: 'CapsLock', eng: 'Caps', width: '75px', finger: 'LP' },
    { code: 'KeyA', eng: 'a', engShift: 'A', hindi: 'ं', hindiShift: '॑', finger: 'LP' },
    { code: 'KeyS', eng: 's', engShift: 'S', hindi: 'ए', hindiShift: 'ऐ', finger: 'LR' },
    { code: 'KeyD', eng: 'd', engShift: 'D', hindi: 'क', hindiShift: 'क्', finger: 'LM' },
    { code: 'KeyF', eng: 'f', engShift: 'F', hindi: 'इ', hindiShift: 'थ', finger: 'LI' },
    { code: 'KeyG', eng: 'g', engShift: 'G', hindi: 'ह', hindiShift: 'ळ', finger: 'LI' },
    { code: 'KeyH', eng: 'h', engShift: 'H', hindi: 'ी', hindiShift: 'ी', finger: 'RI' },
    { code: 'KeyJ', eng: 'j', engShift: 'J', hindi: 'र', hindiShift: 'श्र', finger: 'RI' },
    { code: 'KeyK', eng: 'k', engShift: 'K', hindi: 'ा', hindiShift: 'ा', finger: 'RM' },
    { code: 'KeyL', eng: 'l', engShift: 'L', hindi: 'स', hindiShift: 'स्', finger: 'RR' },
    { code: 'Semicolon', eng: ';', engShift: ':', hindi: 'य', hindiShift: 'रू', finger: 'RP' },
    { code: 'Quote', eng: "'", engShift: '"', hindi: 'श', hindiShift: 'ष्', finger: 'RP' },
    { code: 'Enter', eng: 'Enter', width: '85px', finger: 'RP' }
  ],
  // Row 4
  [
    { code: 'ShiftLeft', eng: 'Shift', width: '100px', finger: 'LP' },
    { code: 'KeyZ', eng: 'z', engShift: 'Z', hindi: '्र', hindiShift: 'प्र', finger: 'LP' },
    { code: 'KeyX', eng: 'x', engShift: 'X', hindi: 'ग', hindiShift: 'ग्', finger: 'LR' },
    { code: 'KeyC', eng: 'c', engShift: 'C', hindi: 'ब', hindiShift: 'ब्', finger: 'LM' },
    { code: 'KeyV', eng: 'v', engShift: 'V', hindi: 'अ', hindiShift: 'ट', finger: 'LI' },
    { code: 'KeyB', eng: 'b', engShift: 'B', hindi: 'व', hindiShift: 'ठ', finger: 'LI' },
    { code: 'KeyN', eng: 'n', engShift: 'N', hindi: 'ल', hindiShift: 'छ', finger: 'RI' },
    { code: 'KeyM', eng: 'm', engShift: 'M', hindi: 'न', hindiShift: 'ड', finger: 'RI' },
    { code: 'Comma', eng: ',', engShift: '<', hindi: 'ए', hindiShift: 'ढ', finger: 'RM' },
    { code: 'Period', eng: '.', engShift: '>', hindi: '।', hindiShift: 'झ', finger: 'RR' },
    { code: 'Slash', eng: '/', engShift: '?', hindi: 'ध', hindiShift: 'घ', finger: 'RP' },
    { code: 'ShiftRight', eng: 'Shift', width: '115px', finger: 'RP' }
  ],
  // Row 5
  [
    { code: 'Space', eng: ' ', width: '350px', finger: 'Thumb' }
  ]
];

const findKeyForChar = (char, mode) => {
  for (const row of KEYBOARD_LAYOUT) {
    for (const key of row) {
      if (mode === 'english') {
        if (key.eng === char) return { key, shift: false };
        if (key.engShift === char) return { key, shift: true };
      } else {
        if (key.hindi === char) return { key, shift: false };
        if (key.hindiShift === char) return { key, shift: true };
      }
    }
  }
  // Handle space separately
  if (char === ' ') return { key: KEYBOARD_LAYOUT[4][0], shift: false };
  return null;
};

export default function TypingKeyboard({ targetChar, mode }) {
  const highlight = findKeyForChar(targetChar, mode);
  const activeFinger = highlight?.key?.finger;

  return (
    <div style={{ padding: '12px 16px', background: C.bg, borderRadius: 12, marginTop: 16, border: `1px solid ${C.border}`, width: 'fit-content', margin: '16px auto 0' }}>
      {/* Finger Guide Text */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, gap: 12 }}>
        {Object.entries(FINGERS).map(([code, finger]) => (
          <div key={code} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 4, 
            opacity: activeFinger === code ? 1 : 0.25,
            transform: activeFinger === code ? 'scale(1.05)' : 'scale(1)',
            transition: '0.2s'
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: finger.color }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>{finger.name.split(' ')[1] || finger.name}</span>
          </div>
        ))}
      </div>

      {/* Actual Keyboard */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
        {KEYBOARD_LAYOUT.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 4 }}>
            {row.map(key => {
              const isActive = highlight?.key?.code === key.code;
              const isShiftNeeded = highlight?.shift;
              const fingerColor = FINGERS[key.finger]?.color || '#fff';
              
              const isShiftKey = key.code === 'ShiftLeft' || key.code === 'ShiftRight';
              const showShiftHighlight = isShiftNeeded && isShiftKey;

              // Scale width for smaller keys
              const baseWidth = 36;
              let width = baseWidth;
              if (key.width) {
                const numericWidth = parseInt(key.width);
                width = Math.round((numericWidth / 45) * baseWidth);
              }

              return (
                <div 
                  key={key.code}
                  style={{
                    width: `${width}px`,
                    height: '36px',
                    background: isActive || showShiftHighlight ? fingerColor : '#fff',
                    color: isActive || showShiftHighlight ? '#fff' : C.text,
                    border: `1px solid ${isActive || showShiftHighlight ? fingerColor : C.border}`,
                    borderRadius: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 10,
                    fontWeight: 700,
                    transition: '0.1s',
                    position: 'relative'
                  }}
                >
                  {mode === 'english' ? (
                    <>
                      <div style={{ fontSize: 8, opacity: 0.6, lineHeight: 1 }}>{key.engShift}</div>
                      <div style={{ lineHeight: 1 }}>{key.eng}</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 8, opacity: 0.6, lineHeight: 1 }}>{key.hindiShift}</div>
                      <div style={{ lineHeight: 1 }}>{key.hindi || key.eng}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: C.muted }}>
        Press <b style={{ color: C.accent }}>{highlight?.key?.eng?.toUpperCase() || 'Space'}</b> 
        {highlight?.shift ? ' + Shift' : ''} 
        with <b style={{ color: FINGERS[activeFinger]?.color }}>{FINGERS[activeFinger]?.name || '...'}</b>
      </div>
    </div>
  );
}
